import { type ApiContainerLine, type ApiOrder } from '@Model';
import { useMemo, useState } from 'react';
import { type ContainerOrderState, isForeignToSite, numbersOf } from './containerRowState';

/** What stands and is planned at the order's site. */
export interface SiteContainers {
    standing: ApiContainerLine[];
    planned: ApiContainerLine[];
}

/**
 * What the container actions read from the order. The numbers are taken when editing starts, that is when
 * the draft arrives with its own id, and for a row added later when it first appears.
 */
// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function useContainerOrderState(
    order: ApiOrder,
    canEdit: boolean,
    site: SiteContainers | undefined,
    correcting: boolean,
): ContainerOrderState {
    const actions = order.container_actions;
    const edit = `${canEdit}|${order.id}`;
    const [snapshotOf, setSnapshotOf] = useState(edit);
    const [numbersAtEditStart, setNumbersAtEditStart] = useState(() => numbersOf(actions));
    const added = actions.filter((action) => !numbersAtEditStart.has(action.id));
    if (edit !== snapshotOf) {
        setSnapshotOf(edit);
        setNumbersAtEditStart(numbersOf(actions));
    } else if (added.length > 0) {
        setNumbersAtEditStart(new Map([...numbersAtEditStart, ...numbersOf(added)]));
    }

    const foreignToSite = useMemo(
        () =>
            new Set(
                site === undefined
                    ? []
                    : actions
                          .filter((action) => isForeignToSite(action, site.standing, site.planned))
                          .map((action) => action.id),
            ),
        [actions, site],
    );

    return useMemo(
        () => ({
            completedAt: order.completed_at,
            positions: order.positions,
            numbersAtEditStart,
            foreignToSite,
            correcting,
        }),
        [order.completed_at, order.positions, numbersAtEditStart, foreignToSite, correcting],
    );
}
